import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import api, { getStoredUser, clearAuthTokens, isAuthenticated } from '@/lib/api';
import Login from './pages/Login';
import AccessDenied from './pages/AccessDenied';
import TermsOfService from './pages/TermsOfService';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetails from './pages/ProjectDetails';
import Clients from './pages/Clients';
import UserManagement from './pages/UserManagement';
import CalendarView from './pages/CalendarView';
import TaskCalendar from './pages/TaskCalendar';
import Analytics from './pages/Analytics';
import Reports from './pages/Reports';
import Evolution from './pages/Evolution';
import Financial from './pages/Financial';
import Budgets from './pages/Budgets';
import DeadlineControl from './pages/DeadlineControl';
import ProjectTemplates from './pages/ProjectTemplates';
import ClientDashboard from './pages/ClientDashboard';
import Performance from './pages/Performance';
import Priority from './pages/Priority';
import HealthCheck from './pages/HealthCheck';
import MyProfile from './pages/MyProfile';
import Home from './pages/Home';
import Notifications from './pages/Notifications';
import Diagnostics from './pages/Diagnostics';
import TimeTracking from './pages/TimeTracking';
import McpConsent from './pages/McpConsent';
import McpHub from './pages/McpHub';
import '@/App.css';

function ProtectedRoute({ children }) {
  const [authState, setAuthState] = useState({ loading: true, user: null });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      // First check if we have a token
      if (!isAuthenticated()) {
        setAuthState({ loading: false, user: null });
        const next = encodeURIComponent(location.pathname + location.search);
        navigate(`/login?next=${next}`);
        return;
      }

      // Try to get stored user first
      const storedUser = getStoredUser();
      if (storedUser) {
        setAuthState({ loading: false, user: storedUser });
        return;
      }

      // If no stored user, fetch from API
      try {
        const response = await api.get('/auth/me');
        setAuthState({ loading: false, user: response.data });
      } catch (error) {
        console.error('Auth check error:', error);
        clearAuthTokens();
        setAuthState({ loading: false, user: null });
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate]);

  if (authState.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
      </div>
    );
  }

  if (!authState.user) {
    return null;
  }

  return React.cloneElement(children, { user: authState.user });
}

/**
 * Wraps a route to require one of the given roles. If the current user's role
 * is not allowed, redirects to /access-denied.
 */
function RoleGuard({ allow, children, user }) {
  const role = (user?.role || '').toUpperCase();
  if (!allow.map((r) => r.toUpperCase()).includes(role)) {
    return <Navigate to="/access-denied" replace />;
  }
  // Forward the user prop to the wrapped page
  return React.cloneElement(children, { user });
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route
        path="/dashboard"
        element={<Navigate to="/home" replace />}
      />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meus-projetos"
        element={
          <ProtectedRoute>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <Projects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <ProtectedRoute>
            <ProjectDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <Clients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <RoleGuard allow={['ADMIN', 'CONSULTANT']}>
              <UserManagement />
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/task-calendar"
        element={
          <ProtectedRoute>
            <TaskCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/evolution"
        element={
          <ProtectedRoute>
            <Evolution />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financial"
        element={
          <ProtectedRoute>
            <Financial />
          </ProtectedRoute>
        }
      />
      <Route
        path="/budgets"
        element={
          <ProtectedRoute>
            <Budgets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deadline-control"
        element={
          <ProtectedRoute>
            <DeadlineControl />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project-templates"
        element={
          <ProtectedRoute>
            <ProjectTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/desempenho"
        element={
          <ProtectedRoute>
            <RoleGuard allow={['ADMIN', 'CONSULTANT']}>
              <Performance />
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prioridades"
        element={
          <ProtectedRoute>
            <RoleGuard allow={['ADMIN']}>
              <Priority />
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/health"
        element={
          <ProtectedRoute>
            <RoleGuard allow={['ADMIN']}>
              <HealthCheck />
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mcp/consent"
        element={
          <ProtectedRoute>
            <McpConsent />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mcp"
        element={
          <ProtectedRoute>
            <RoleGuard allow={['ADMIN']}>
              <McpHub />
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route path="/mcp/autorizacoes" element={<Navigate to="/mcp" replace />} />
      <Route path="/mcp/auditoria" element={<Navigate to="/mcp?aba=auditoria" replace />} />
      <Route
        path="/meu-cadastro"
        element={
          <ProtectedRoute>
            <MyProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <RoleGuard allow={['ADMIN']}>
              <Notifications />
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/diagnostics"
        element={
          <ProtectedRoute>
            <RoleGuard allow={['ADMIN']}>
              <Diagnostics />
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-tracking"
        element={
          <ProtectedRoute>
            <RoleGuard allow={['ADMIN', 'CONSULTANT']}>
              <TimeTracking />
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/forest-inventory/*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
